import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Button, DateRangeFilter } from '@/components';
import { SHIPMENT_STATUSES, type ShipmentExim } from '@/services/api/shipments';
import { getShipmentStatusLabel } from './ShipmentStatusBadge';
import { getShipmentEximLabel } from './ShipmentEximBadge';

export interface ShipmentFilterValue {
  status: string; // single ShipmentStatus, '' = all (BE accepts one value)
  exim: ShipmentExim | ''; // EXIM direction tag, '' = all directions
  startDate: string; // created date range (createdFrom)
  endDate: string; // created date range (createdTo)
}

export const EMPTY_FILTER: ShipmentFilterValue = {
  status: '',
  exim: '',
  startDate: '',
  endDate: '',
};

export const countActiveFilters = (v: ShipmentFilterValue): number =>
  (v.status ? 1 : 0) + (v.exim ? 1 : 0) + (v.startDate || v.endDate ? 1 : 0);

// Direction tag choices for the filter — the same set the export picker uses.
const EXIM_OPTIONS: ShipmentExim[] = ['EXPORT', 'IMPORT', 'GENERAL'];

const sectionLabel =
  'mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400';

const toISODate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
const fromISODate = (s: string) => (s ? new Date(s) : null);

// Quick created-date presets (mirrors the prototype's chip row). Each resolves
// to a concrete { start, end } range when clicked.
const presetRange = (fromDaysAgo: number, toDaysAgo = 0) => ({
  start: dayjs().subtract(fromDaysAgo, 'day').format('YYYY-MM-DD'),
  end: dayjs().subtract(toDaysAgo, 'day').format('YYYY-MM-DD'),
});

const CREATED_PRESETS: { label: string; range: () => { start: string; end: string } }[] = [
  { label: 'Today', range: () => presetRange(0, 0) },
  { label: 'Yesterday', range: () => presetRange(1, 1) },
  { label: 'Last 7 Days', range: () => presetRange(7, 0) },
  { label: 'Last 30 Days', range: () => presetRange(30, 0) },
];

// Matches the prototype's .chip: 7px/13px padding, 8px radius, 12.5px
// semibold, solid-primary fill when active.
const presetChip = (active: boolean) =>
  `rounded-lg border px-3.5 py-[7px] text-[12.5px] font-semibold transition-colors ${
    active
      ? 'border-primary bg-primary text-static-white'
      : 'border-stroke-sub-300 text-text-sub-600 hover:border-primary'
  }`;

interface ShipmentFilterProps {
  value: ShipmentFilterValue;
  onApply: (value: ShipmentFilterValue) => void;
}

const ShipmentFilter: React.FC<ShipmentFilterProps> = ({ value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ShipmentFilterValue>(value);

  // Re-seed the draft from the applied value each time the drawer opens.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const activeCount = countActiveFilters(value);

  // Single-select: re-clicking the active status clears it (back to "all").
  const selectStatus = (v: string) =>
    setDraft((d) => ({ ...d, status: d.status === v ? '' : v }));

  const handleCreatedChange = (range: { from: Date | null; to: Date | null }) =>
    setDraft((d) => ({
      ...d,
      startDate: toISODate(range.from),
      endDate: toISODate(range.to),
    }));

  // Toggle a preset chip — clicking the active one clears the range.
  const applyPreset = (r: { start: string; end: string }, active: boolean) =>
    setDraft((d) =>
      active
        ? { ...d, startDate: '', endDate: '' }
        : { ...d, startDate: r.start, endDate: r.end }
    );

  const apply = () => {
    onApply(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft(EMPTY_FILTER);
    onApply(EMPTY_FILTER);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-2 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-sub-600 hover:bg-bg-weak-50"
      >
        <i className="ri-filter-3-line" />
        Filter
        {activeCount > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-static-white">
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-bg-strong-950/40"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />

            {/* Right drawer */}
            <div className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col bg-bg-white-0 shadow-card-lg">
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-stroke-soft-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-strong-950">
                    Filter
                  </h2>
                  <p className="text-xs text-text-sub-600">Shipments</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-text-soft-400 hover:text-text-sub-600"
                  aria-label="Close filter"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-5">
                {/* Created date */}
                <div>
                  <h4 className={sectionLabel}>Created date</h4>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {CREATED_PRESETS.map((p) => {
                      const r = p.range();
                      const active =
                        draft.startDate === r.start && draft.endDate === r.end;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => applyPreset(r, active)}
                          className={presetChip(active)}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Remount the picker when the chip-driven range changes so its
                      button label/initialRange stay in sync. Opens downward
                      (default) since this section is at the top of the drawer. */}
                  <DateRangeFilter
                    key={`${draft.startDate}-${draft.endDate}`}
                    onDateRangeChange={handleCreatedChange}
                    initialRange={{
                      from: fromISODate(draft.startDate),
                      to: fromISODate(draft.endDate),
                    }}
                  />
                </div>

                {/* Status */}
                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className={sectionLabel + ' mb-0'}>Status</h4>
                    {draft.status && (
                      <button
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, status: '' }))}
                        className="text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 rounded-lg border border-stroke-soft-200 p-3">
                    {SHIPMENT_STATUSES.map((s) => {
                      const checked = draft.status === s;
                      return (
                        <label
                          key={s}
                          className="flex cursor-pointer items-center gap-2.5 py-1"
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                              checked
                                ? 'border-primary bg-primary'
                                : 'border-stroke-sub-300 bg-bg-white-0'
                            }`}
                          >
                            <input
                              type="radio"
                              name="shipment-status"
                              className="sr-only"
                              checked={checked}
                              onChange={() => selectStatus(s)}
                            />
                            {checked && (
                              <span className="h-1.5 w-1.5 rounded-full bg-static-white" />
                            )}
                          </span>
                          <span className="text-sm text-text-strong-950">
                            {getShipmentStatusLabel(s)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Direction (EXIM tag) — single-select, re-click clears. */}
                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className={sectionLabel + ' mb-0'}>Arah / tag</h4>
                    {draft.exim && (
                      <button
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, exim: '' }))}
                        className="text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 rounded-lg border border-stroke-soft-200 p-3">
                    {EXIM_OPTIONS.map((e) => {
                      const checked = draft.exim === e;
                      return (
                        <label
                          key={e}
                          className="flex cursor-pointer items-center gap-2.5 py-1"
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                              checked
                                ? 'border-primary bg-primary'
                                : 'border-stroke-sub-300 bg-bg-white-0'
                            }`}
                          >
                            <input
                              type="radio"
                              name="shipment-exim"
                              className="sr-only"
                              checked={checked}
                              onChange={() =>
                                setDraft((d) => ({
                                  ...d,
                                  exim: d.exim === e ? '' : e,
                                }))
                              }
                            />
                            {checked && (
                              <span className="h-1.5 w-1.5 rounded-full bg-static-white" />
                            )}
                          </span>
                          <span className="text-sm text-text-strong-950">
                            {getShipmentEximLabel(e)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-between border-t border-stroke-soft-200 px-5 py-4">
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
                >
                  Reset
                </button>
                <Button variant="primary" size="medium" onClick={apply}>
                  Apply filter
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default ShipmentFilter;
